/**
 * RecordingItem — shows one Live Class recording row with:
 *  - inline player (respects trim_start/trim_end metadata)
 *  - chapters jumper (Whisper transcription)
 *  - student watch-tracking (auto-pings backend every 15 s while playing)
 *  - teacher analytics, trim editor, transcribe button, delete
 *
 * Designed to keep LiveClassroomRoom lean.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';
import {
  Film, Download, ExternalLink, Trash2, Captions, ListVideo, Loader2,
  Sparkles, Scissors, BarChart3, Play, Pause,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const fmt = (s) => {
  if (!s && s !== 0) return '--:--';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export default function RecordingItem({ rec, tenantId, sessionId, isTeacher, studentId, openAt = null, onChange }) {
  const qc = useQueryClient();
  const videoRef = useRef(null);
  const [open, setOpen] = useState(Boolean(openAt));
  const [trimOpen, setTrimOpen] = useState(false);
  const [trimStart, setTrimStart] = useState(rec.trim_start_seconds || 0);
  const [trimEnd, setTrimEnd] = useState(rec.trim_end_seconds || 0);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  // Auto-tick view tracker — every 15 s while playing, students post a view event
  useEffect(() => {
    if (!open || !studentId) return;
    let tick = null;
    const start = () => {
      if (tick) return;
      tick = setInterval(() => {
        const v = videoRef.current;
        if (!v || v.paused) return;
        const watched = Math.round(v.currentTime || 0);
        api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/view`, {
          method: 'POST',
          body: JSON.stringify({
            student_id: studentId,
            watched_seconds: watched,
            completed: v.duration && watched >= v.duration - 5,
          }),
        }).catch(() => {});
      }, 15000);
    };
    const v = videoRef.current;
    if (v) {
      v.addEventListener('play', start);
      v.addEventListener('pause', () => { if (tick) { clearInterval(tick); tick = null; } });
      v.addEventListener('ended', () => { if (tick) { clearInterval(tick); tick = null; }
        // Send a final completed event
        api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/view`, {
          method: 'POST',
          body: JSON.stringify({ student_id: studentId, watched_seconds: Math.round(v.duration || 0), completed: true }),
        }).catch(() => {});
      });
    }
    return () => { if (tick) clearInterval(tick); };
  }, [open, rec.id, sessionId, tenantId, studentId]);

  // Respect trim markers: jump to start, pause at end. And pick up where the
  // pupil left off — asking a student to scrub back to minute 38 of a lecture
  // they half-watched is how a catch-up goes unwatched.
  useEffect(() => {
    if (!open) return;
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => {
      if (openAt && openAt > 0) {
        v.currentTime = openAt;          // came from a search: the exact moment
        v.play().catch(() => {});
        return;
      }
      const resume = Number(rec.resume_seconds || 0);
      if (resume > 10 && !rec.watched_to_the_end
          && (!v.duration || resume < v.duration - 10)) {
        v.currentTime = resume;
      } else if (rec.trim_start_seconds) {
        v.currentTime = rec.trim_start_seconds;
      }
    };
    const onTime = () => {
      if (rec.trim_end_seconds && v.currentTime >= rec.trim_end_seconds) {
        v.pause();
        v.currentTime = rec.trim_end_seconds;
      }
    };
    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('timeupdate', onTime);
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('timeupdate', onTime);
    };
  }, [open, rec.trim_start_seconds, rec.trim_end_seconds, rec.resume_seconds, rec.watched_to_the_end, openAt]);

  const trimMutation = useMutation({
    mutationFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/trim`, {
      method: 'PUT',
      body: JSON.stringify({ trim_start_seconds: Number(trimStart) || 0, trim_end_seconds: Number(trimEnd) || 0 }),
    }),
    onSuccess: () => { toast.success('Trim saved'); setTrimOpen(false); onChange?.(); qc.invalidateQueries({ queryKey: ['live-recordings'] }); },
    onError: (e) => toast.error(e.message),
  });

  const transcribeMutation = useMutation({
    mutationFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/transcribe`, { method: 'POST' }),
    onSuccess: () => { toast.success('Transcription queued — refresh in ~30 s'); onChange?.(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Recording deleted'); onChange?.(); },
    onError: (e) => toast.error(e.message),
  });

  const { data: analytics, refetch: reAnalytics } = useQuery({
    queryKey: ['rec-analytics', rec.id],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/analytics`),
    enabled: analyticsOpen && isTeacher,
  });

  const { data: transcript } = useQuery({
    queryKey: ['rec-transcript', rec.id],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings/${rec.id}/transcript`),
    enabled: transcriptOpen,
  });

  const seekTo = (sec) => {
    if (!open) setOpen(true);
    setTimeout(() => {
      const v = videoRef.current;
      if (v) { v.currentTime = Math.max(0, sec); v.play().catch(() => {}); }
    }, 200);
  };

  const transcriptStatus = rec.transcript_status || '';
  const chapters = rec.chapters || [];

  return (
    <li className="text-sm border-b last:border-0 pb-3 last:pb-0" data-testid={`recording-row-${rec.id}`}>
      <div className="flex items-start gap-2">
        <Film className="h-4 w-4 text-slate-400 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{rec.filename}</div>
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-2 gap-y-0.5 items-center">
            <span>{(rec.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
            {rec.duration_seconds ? <span>· {fmt(rec.duration_seconds)}</span> : null}
            {rec.uploader_name ? <span>· {rec.uploader_name}</span> : null}
            {rec.trim_start_seconds || rec.trim_end_seconds ? (
              <Badge variant="outline" className="text-[10px]" data-testid={`trim-badge-${rec.id}`}>
                <Scissors className="h-2.5 w-2.5 mr-0.5" /> {fmt(rec.trim_start_seconds || 0)} – {fmt(rec.trim_end_seconds || rec.duration_seconds || 0)}
              </Badge>
            ) : null}
            {transcriptStatus && (
              <Badge
                variant="outline"
                className={`text-[10px] ${transcriptStatus === 'done' ? 'text-emerald-700 border-emerald-300' : transcriptStatus === 'error' ? 'text-rose-700 border-rose-300' : 'text-slate-600'}`}
                data-testid={`transcript-status-${rec.id}`}
              >
                <Captions className="h-2.5 w-2.5 mr-0.5" /> {transcriptStatus}
              </Badge>
            )}
            {chapters.length > 0 && (
              <Badge variant="outline" className="text-[10px]"><ListVideo className="h-2.5 w-2.5 mr-0.5" /> {chapters.length} chapter{chapters.length === 1 ? '' : 's'}</Badge>
            )}
            {Number(rec.resume_seconds || 0) > 10 && !rec.watched_to_the_end && (
              <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300"
                     data-testid={`recording-resume-${rec.id}`}>
                <Play className="h-2.5 w-2.5 mr-0.5" /> resumes at {fmt(rec.resume_seconds)}
              </Badge>
            )}
            {rec.watched_to_the_end && (
              <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300"
                     data-testid={`recording-watched-${rec.id}`}>
                watched
              </Badge>
            )}
          </div>
        </div>
        <Button size="sm" variant={open ? 'default' : 'ghost'} className="h-7 px-2" onClick={() => setOpen((o) => !o)} data-testid={`recording-watch-${rec.id}`}>
          {open ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </Button>
        {chapters.length > 0 && (
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setTranscriptOpen(true)} title="Transcript & chapters" data-testid={`recording-transcript-${rec.id}`}>
            <Captions className="h-3.5 w-3.5" />
          </Button>
        )}
        <a href={`${API_URL}${rec.url}`} target="_blank" rel="noreferrer">
          <Button size="sm" variant="ghost" className="h-7 px-2" title="Download"><Download className="h-3.5 w-3.5" /></Button>
        </a>
        {rec.external_url && (
          <a href={rec.external_url} target="_blank" rel="noreferrer">
            <Button size="sm" variant="ghost" className="h-7 px-2" title="External mirror"><ExternalLink className="h-3.5 w-3.5" /></Button>
          </a>
        )}
        {isTeacher && (
          <>
            <Button size="sm" variant="ghost" className="h-7 px-2" title="Trim" data-testid={`recording-trim-${rec.id}`} onClick={() => { setTrimStart(rec.trim_start_seconds || 0); setTrimEnd(rec.trim_end_seconds || rec.duration_seconds || 0); setTrimOpen(true); }}>
              <Scissors className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" title="Analytics" data-testid={`recording-analytics-${rec.id}`} onClick={() => { setAnalyticsOpen(true); reAnalytics(); }}>
              <BarChart3 className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2" title="Transcribe with Whisper" data-testid={`recording-transcribe-${rec.id}`} disabled={transcribeMutation.isPending} onClick={() => transcribeMutation.mutate()}>
              {transcribeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600 hover:bg-rose-50" title="Delete" data-testid={`recording-delete-${rec.id}`}
              onClick={() => { if (window.confirm('Delete this recording?')) deleteMutation.mutate(); }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          <video
            ref={videoRef}
            src={`${API_URL}${rec.url}`}
            controls
            className="w-full max-h-[420px] bg-black rounded-md"
            data-testid={`recording-video-${rec.id}`}
          />
          {chapters.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid={`chapters-${rec.id}`}>
              {chapters.map((c, i) => (
                <Button key={i} variant="outline" size="sm" className="h-7 text-xs" onClick={() => seekTo(c.start)}>
                  <ListVideo className="h-3 w-3 mr-1 text-indigo-600" /> {fmt(c.start)} · {c.title}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trim dialog */}
      <Dialog open={trimOpen} onOpenChange={setTrimOpen}>
        <DialogContent data-testid="recording-trim-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Scissors className="h-4 w-4" /> Trim {rec.filename}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-xs text-slate-500">Set logical start/end markers — the player will skip the rest. No re-encoding needed.</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-600">Start (seconds)</label>
                <Input type="number" min={0} value={trimStart} onChange={(e) => setTrimStart(e.target.value)} data-testid="trim-start-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600">End (seconds)</label>
                <Input type="number" min={0} value={trimEnd} onChange={(e) => setTrimEnd(e.target.value)} data-testid="trim-end-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrimOpen(false)}>Cancel</Button>
            <Button onClick={() => trimMutation.mutate()} disabled={trimMutation.isPending} data-testid="trim-save-btn">
              {trimMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save trim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics dialog */}
      <Dialog open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
        <DialogContent data-testid="recording-analytics-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Recording analytics</DialogTitle></DialogHeader>
          {!analytics ? (
            <div className="text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center">
                <div className="p-2 rounded bg-emerald-50"><div className="text-xl font-bold text-emerald-700">{analytics.viewers}</div><div className="text-xs">Viewers</div></div>
                <div className="p-2 rounded bg-indigo-50"><div className="text-xl font-bold text-indigo-700">{analytics.completed_count}</div><div className="text-xs">Completed</div></div>
                <div className="p-2 rounded bg-amber-50"><div className="text-xl font-bold text-amber-700">{fmt(analytics.average_watched_seconds)}</div><div className="text-xs">Avg watched</div></div>
              </div>
              <div className="text-xs text-slate-500">Total play sessions: {analytics.total_view_count}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transcript dialog */}
      <Dialog open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <DialogContent className="max-w-2xl" data-testid="recording-transcript-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Captions className="h-4 w-4" /> Transcript</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm max-h-[60vh] overflow-y-auto">
            {!transcript ? (
              <div className="text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
            ) : transcript.transcript_status !== 'done' ? (
              <div className="text-amber-700">Transcript status: {transcript.transcript_status || 'pending'}</div>
            ) : (
              <>
                {(transcript.chapters || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase mb-1">Chapters</p>
                    <ul className="space-y-1">
                      {transcript.chapters.map((c, i) => (
                        <li key={i}><button className="text-indigo-600 hover:underline text-left" onClick={() => { setTranscriptOpen(false); seekTo(c.start); }}>{fmt(c.start)} · {c.title}</button></li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold uppercase mb-1">Full transcript</p>
                  <p className="whitespace-pre-wrap text-slate-700">{transcript.transcript}</p>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </li>
  );
}
